import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { importSetupCostPrices, SetupApiError, SetupCostPriceImportResult } from '../../../services/accountSetup/accountSetupService';
import { secondaryText, textDarkGreen } from '../../../styling/stylingHelper';
import { setupButton, setupSecondary, setupInput, SetupError, SetupModal } from './SetupPrimitives';

export default function SetupCostPriceImport({ restaurantId, disabled, onImported }: {
  restaurantId: string; disabled: boolean; onImported: (run: { id: string; name: string } | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const base = 'accountSetup.costPriceImport';
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [scope, setScope] = useState<'all' | 'food' | 'drinks'>('all');
  const [vat, setVat] = useState<'9' | '21'>('9');
  const [variant, setVariant] = useState<'standard' | 'new_menu'>('standard');
  const [menuName, setMenuName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<SetupCostPriceImportResult | null>(null);
  const submit = async () => {
    if (busy || !file || result) return;
    if (!file.name.toLowerCase().endsWith('.docx') || file.size > 25 * 1024 * 1024 || file.size === 0) { setError(`${base}.fileError`); return; }
    setBusy(true); setError('');
    try {
      const imported = await importSetupCostPrices(restaurantId, file, { overallType: scope, vatRate: vat, variant, newMenuName: menuName });
      setResult(imported);
      await onImported(imported.runId ? { id: imported.runId, name: file.name.replace(/\.docx$/i, '') } : null);
      setOpen(false);
      setFile(null);
    } catch (failure) {
      const code = failure instanceof SetupApiError ? failure.code : '';
      setError(code === 'duplicate_menu_name' ? 'costPrice.newMenuAutomation.duplicate_menu_name'
        : ['invalid_cost_price_file', 'file_too_large'].includes(code) ? `${base}.fileError` : `${base}.failed`);
    } finally { setBusy(false); }
  };
  return <>
    <button className={setupSecondary} disabled={disabled} onClick={() => { setOpen(true); setFile(null); setResult(null); setError(''); }}><Upload className="h-4 w-4" />{t(`${base}.open`)}</button>
        {result && <div role="status" className="space-y-2"><p className={result.runId ? textDarkGreen : secondaryText}>{t(`${base}.${result.runId ? 'success' : 'noRun'}`)}</p>{result.menuAssignmentFailed && <p className={`text-sm ${secondaryText}`}>{t(`${base}.menuWarning`)}</p>}</div>}
    {open && <SetupModal title={t(`${base}.title`)} description={t(`${base}.description`)} busy={busy} onClose={() => setOpen(false)}>
      <div className="space-y-4">
        {!result && <>
          <label className="block text-sm">{t(`${base}.file`)}<input type="file" accept=".docx" disabled={busy} className={`${setupInput} mt-1`} onChange={event => { setFile(event.target.files?.[0] ?? null); setError(''); }} /></label>
          <label className="block text-sm">{t(`${base}.variant`)}<select disabled={busy} className={`${setupInput} mt-1`} value={variant} onChange={event => setVariant(event.target.value as typeof variant)}><option value="standard">{t(`${base}.standard`)}</option><option value="new_menu">{t(`${base}.newMenu`)}</option></select></label>
          <p className={`text-sm ${secondaryText}`}>{t(`${base}.${variant === 'new_menu' ? 'newMenuHelp' : 'standardHelp'}`)}</p>
          {variant === 'new_menu' && <label className="block text-sm">{t(`${base}.menuName`)}<input className={`${setupInput} mt-1`} disabled={busy} value={menuName} maxLength={100} onChange={event => setMenuName(event.target.value)} /></label>}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">{t(`${base}.scope`)}<select disabled={busy} className={`${setupInput} mt-1`} value={scope} onChange={event => { setScope(event.target.value as typeof scope); setVat(event.target.value === 'drinks' ? '21' : '9'); }}>{(['all', 'food', 'drinks'] as const).map(value => <option value={value} key={value}>{t(`${base}.${value}`)}</option>)}</select></label>
            <label className="block text-sm">{t(`${base}.vat`)}<select disabled={busy} className={`${setupInput} mt-1`} value={vat} onChange={event => setVat(event.target.value as typeof vat)}><option value="9">9%</option><option value="21">21%</option></select></label>
          </div>
          <p className={`text-sm ${secondaryText}`}>{t(`${base}.vatHelp`)}</p>
        </>}

        <SetupError message={error} />
        <div className="flex flex-wrap gap-3">
          {!result && <button className={setupButton} disabled={busy || !file || (variant === 'new_menu' && !menuName.trim())} onClick={() => void submit()}>{t(`${base}.${busy ? 'processing' : 'submit'}`)}</button>}
          <button className={setupSecondary} disabled={busy} onClick={() => setOpen(false)}>{t('common.close')}</button>
        </div>
        {busy && <p role="status" className={`text-sm ${secondaryText}`}>{t(`${base}.processingHelp`)}</p>}
      </div>
    </SetupModal>}
  </>;
}
